// Garde de synchro : un RDV Google Calendar ne doit réécrire la relance que s'il est à venir.
// Un RDV passé laisse intacte la date posée après l'appel (ex. « À relancer dans 3 jours »).

const PARIS = "Europe/Paris";

/** Jour calendaire de Paris (`YYYY-MM-DD`) d'un instant ISO ; `null` si la date est illisible. */
export function parisDateOf(iso: string): string | null {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : parisDate(d);
}

function parisDate(date: Date): string {
  const out: Record<string, string> = {};
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: PARIS,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  for (const p of fmt.formatToParts(date)) out[p.type] = p.value;
  return `${out.year}-${out.month}-${out.day}`;
}

/**
 * `null` si le RDV est passé (ou illisible) : la synchro n'écrit rien.
 * Sinon la date du RDV (jour calendaire de Paris, `YYYY-MM-DD`) et `"Appel " + parisTime`.
 */
export function followUpFromEvent(
  startIso: string,
  now: Date,
  parisTime: string,
): { nextFollowUpAt: string; nextAction: string } | null {
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) return null;
  if (start.getTime() < now.getTime()) return null;
  return { nextFollowUpAt: parisDate(start), nextAction: `Appel ${parisTime}` };
}

export interface UpcomingCandidate {
  id: string;
  email: string;
  startIso: string;
  cancelled: boolean;
}

/**
 * Pour chaque e-mail (minuscules), l'id du RDV confirmé à venir le plus proche.
 * Sans cela, le dernier événement traité (ordre croissant) l'emporterait sur la relance.
 */
export function nearestUpcomingByEmail(
  events: UpcomingCandidate[],
  now: Date,
): Map<string, string> {
  const best = new Map<string, { id: string; t: number }>();
  for (const e of events) {
    if (e.cancelled) continue;
    const t = new Date(e.startIso).getTime();
    if (Number.isNaN(t) || t < now.getTime()) continue;
    const key = e.email.toLowerCase();
    const cur = best.get(key);
    if (!cur || t < cur.t) best.set(key, { id: e.id, t });
  }
  return new Map([...best].map(([k, v]) => [k, v.id]));
}

/** `YYYY-MM-DD` + n jours (calendrier civil, sans fuseau). `null` si la date est illisible. */
export function addDaysIso(isoDate: string, days: number): string | null {
  const m = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) + days * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

/** Une relance dépassée de plus de ce nombre de jours fait passer le prospect en « ancien ». */
export const STALE_AFTER_DAYS = 7;

export const CLOSED_STATUSES = ["closed_won", "closed_lost", "rembourse"];

/**
 * Prospect « ancien » : relance prévue depuis plus de STALE_AFTER_DAYS jours (jour de Paris `today`).
 * Les prospects clos et ceux sans date de relance ne le sont jamais.
 */
export function isStale(
  p: { status: string; nextFollowUpAt: string | null },
  today: string,
): boolean {
  if (!p.nextFollowUpAt || CLOSED_STATUSES.includes(p.status)) return false;
  const cutoff = addDaysIso(today, -STALE_AFTER_DAYS);
  return cutoff !== null && p.nextFollowUpAt.slice(0, 10) < cutoff;
}
